// Pure colour/font helpers shared by the design controllers.
// No DOM access here: every function takes plain values and returns plain values,
// which keeps them trivially unit-testable and reusable across controllers.

const NAMED_COLORS = {
  black: "#000000",
  white: "#ffffff",
  red: "#ff0000",
  green: "#008000",
  blue: "#0000ff",
  transparent: null
}

// "#abc" / "#aabbcc" -> "#aabbcc", anything else -> null.
export function normalizeHexColor(value) {
  const color = String(value).trim()
  if (/^#[0-9a-f]{6}$/i.test(color)) return color
  if (/^#[0-9a-f]{3}$/i.test(color)) {
    return `#${color[1]}${color[1]}${color[2]}${color[2]}${color[3]}${color[3]}`
  }
  return null
}

// Accepts named colours, hex, or rgb()/rgba() and returns a 6-digit hex (or null).
export function normalizeCssColor(value) {
  const color = String(value).trim().toLowerCase()

  if (Object.prototype.hasOwnProperty.call(NAMED_COLORS, color)) return NAMED_COLORS[color]
  if (/^#[0-9a-f]{6}$/i.test(color)) return color
  if (/^#[0-9a-f]{3}$/i.test(color)) return normalizeHexColor(color)

  const rgbMatch = color.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)$/)
  if (!rgbMatch) return null
  if (rgbMatch[4] && Number(rgbMatch[4]) === 0) return null

  return `#${[rgbMatch[1], rgbMatch[2], rgbMatch[3]].map((part) => {
    return Math.max(0, Math.min(255, Number(part))).toString(16).padStart(2, "0")
  }).join("")}`
}

// Normalises a font-family list so equivalent declarations compare equal,
// e.g. `"Times New Roman", Times, serif` vs `Times New Roman, Times, serif`.
export function normalizeFontFamilyList(value) {
  return String(value).toLowerCase().replace(/["']/g, "").replace(/\s+/g, " ").trim()
}

// Returns the option value whose font-family list matches `value`, or null.
export function matchFontFamily(value, optionValues) {
  const normalizedValue = normalizeFontFamilyList(value)
  return optionValues.find((option) => normalizeFontFamilyList(option) === normalizedValue) || null
}

export function escapeHtml(value) {
  return String(value).replace(/[&<>"']/g, (character) => {
    return {
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      "\"": "&quot;",
      "'": "&#39;"
    }[character]
  })
}
